import React, { useEffect, useRef } from "react";
import { useVirtualizer } from "@tanstack/react-virtual";
import { GridProps, Grid } from "../grid/grid";
import { CalendarProps, Calendar } from "../calendar/calendar";
import { TaskGanttContentProps, TaskGanttContent } from "./task-gantt-content";
import styles from "./gantt.module.css";

export type TaskGanttProps = {
  gridProps: GridProps;
  calendarProps: CalendarProps;
  barProps: TaskGanttContentProps;
  ganttHeight: number;
  scrollY: number;
  scrollX: number;
};
export const TaskGantt: React.FC<TaskGanttProps> = ({
  gridProps,
  calendarProps,
  barProps,
  ganttHeight,
  scrollY,
  scrollX,
}) => {
  const ganttSVGRef = useRef<SVGSVGElement>(null);
  const horizontalContainerRef = useRef<HTMLDivElement>(null);
  const verticalGanttContainerRef = useRef<HTMLDivElement>(null);
  const newBarProps = { ...barProps, svg: ganttSVGRef, ganttHeight, scrollY };

  const buffer = barProps.rowHeight * 10;
  const visibleStartY = scrollY - buffer;
  const visibleEndY = scrollY + ganttHeight + buffer;

  const virtualizer = useVirtualizer({
    count: barProps.tasks.length,
    getScrollElement: () => horizontalContainerRef.current,
    estimateSize: () => barProps.rowHeight,
    overscan: 10
  });

  useEffect(() => {
    if (horizontalContainerRef.current) {
      horizontalContainerRef.current.scrollTop = scrollY;
    }
  }, [scrollY]);

  useEffect(() => {
    if (verticalGanttContainerRef.current) {
      verticalGanttContainerRef.current.scrollLeft = scrollX;
    }
  }, [scrollX]);

  return (
    <div
      className={styles.ganttVerticalContainer}
      ref={verticalGanttContainerRef}
      dir="ltr"
    >
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width={gridProps.svgWidth}
        height={calendarProps.headerHeight}
        fontFamily={barProps.fontFamily}
      >
        <Calendar {...calendarProps} />
      </svg>
      <div
        ref={horizontalContainerRef}
        className={styles.horizontalContainer}
        style={
          ganttHeight
            ? { height: ganttHeight, width: gridProps.svgWidth }
            : { width: gridProps.svgWidth }
        }
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width={gridProps.svgWidth}
          height={virtualizer.getTotalSize()}
          fontFamily={barProps.fontFamily}
          ref={ganttSVGRef}
        >
          <Grid
            {...gridProps}
            virtualItems={virtualizer.getVirtualItems()}
            visibleStartY={visibleStartY}
            visibleEndY={visibleEndY}
          />
          <TaskGanttContent
            {...newBarProps}
            visibleStartY={visibleStartY}
            visibleEndY={visibleEndY}
            virtualItems={virtualizer.getVirtualItems()}
          />
        </svg>
      </div>
    </div>
  );
};
